import { TachographDownloadRow, TachographFile, TachographUnitLabel } from "../models/Tachograph";
import { formatUtcDate, getMostRecentDate, parseDateOrNull } from "./tachographDates";

/** Files without an own origin come from the remote download service. */
const DEFAULT_ORIGIN = "RDL";

/** The API sends the file content either as a byte array or base64 encoded. */
export function binaryToUint8Array(binaryData: number[] | string | undefined): Uint8Array | null {
  if (Array.isArray(binaryData) && binaryData.length > 0) {
    return new Uint8Array(binaryData);
  }

  if (typeof binaryData === "string" && binaryData.length > 0) {
    const decoded = atob(binaryData);
    const bytes = new Uint8Array(decoded.length);
    for (let index = 0; index < decoded.length; index += 1) {
      bytes[index] = decoded.charCodeAt(index);
    }

    return bytes;
  }

  return null;
}

export function pickFileName(file?: TachographFile): string {
  return file?.fileNameDdd ?? file?.fileName ?? file?.fileNameTgd ?? file?.fileNameV1B ?? "";
}

/** When the file was generated by the tachograph. */
export function getFileActivityDate(file?: TachographFile): Date | null {
  return getMostRecentDate(file?.timestampEnd, file?.timestamp, file?.dateTime);
}

/** When the file arrived in Geotab. */
export function getFileUploadDate(file?: TachographFile): Date | null {
  return parseDateOrNull(file?.dateTime);
}

/** The date the next deadline counts from: the upload, otherwise the activity date. */
export function getFileReferenceDate(file?: TachographFile): Date | null {
  return parseDateOrNull(file?.dateTime) ?? getMostRecentDate(file?.timestampEnd, file?.timestamp);
}

/** A file only counts as a readout if it carries a date or a name. */
export function hasFileData(file?: TachographFile): boolean {
  if (!file) return false;
  if (getFileActivityDate(file)) return true;

  return pickFileName(file).trim().length > 0;
}

/** Keeps the newest file per device or driver. */
export function latestFileByEntityId(
  files: TachographFile[],
  getEntityId: (file: TachographFile) => string | undefined,
): Record<string, TachographFile> {
  const latest: Record<string, TachographFile> = {};

  for (const file of files) {
    const entityId = getEntityId(file);
    if (!entityId) continue;

    const candidateDate = getFileActivityDate(file);
    const currentDate = getFileActivityDate(latest[entityId]);

    if (!latest[entityId] || (candidateDate && (!currentDate || candidateDate > currentDate))) {
      latest[entityId] = file;
    }
  }

  return latest;
}

function buildUnitLabel(unit?: TachographUnitLabel): string {
  if (!unit) return "";

  const { name, licensePlate } = unit;
  if (!licensePlate) return name;
  if (name.toLowerCase().includes(licensePlate.toLowerCase())) return name;

  return [licensePlate, name].filter(Boolean).join(" - ");
}

/** The rows of the downloads tab, newest file first. */
export function buildDownloadRows(
  files: TachographFile[],
  unitsByDeviceId: Map<string, TachographUnitLabel>,
): TachographDownloadRow[] {
  return files
    .map(file => {
      const id = file.id?.trim();
      if (!id) return null;

      const dataFrom = parseDateOrNull(file.timestampStart);
      const dataTo = parseDateOrNull(file.timestampEnd) ?? parseDateOrNull(file.timestamp);
      const createdAt = parseDateOrNull(file.timestamp) ?? parseDateOrNull(file.timestampEnd);
      const addedAt = parseDateOrNull(file.dateTime);
      const sortDate = addedAt ?? createdAt ?? dataTo ?? dataFrom;

      return {
        row: {
          id,
          source: file.originDescription?.trim() || DEFAULT_ORIGIN,
          fileName: pickFileName(file),
          unit: buildUnitLabel(file.device?.id ? unitsByDeviceId.get(file.device.id) : undefined),
          dataFromUtc: dataFrom ? formatUtcDate(dataFrom) : "",
          dataToUtc: dataTo ? formatUtcDate(dataTo) : "",
          createdAt: createdAt ? formatUtcDate(createdAt) : "",
          addedAt: addedAt ? formatUtcDate(addedAt) : "",
        },
        sortMs: sortDate?.getTime() ?? 0,
      };
    })
    .filter((entry): entry is { row: TachographDownloadRow; sortMs: number } => entry !== null)
    .sort((a, b) => b.sortMs - a.sortMs)
    .map(entry => entry.row);
}
